import React, { memo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { DateTimePickerButton } from '@/components';
import { GlassCard } from '@/components/shared/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';

export type PublishType = 'publishNow' | 'draft' | 'scheduled';

interface PublishOptionsSectionBaseProps {
  title: string;
  copy: {
    publishNow: { label: string; description: string };
    draft: { label: string; description: string };
    scheduled: { label: string; description: string };
    scheduledFieldLabel?: string;
    scheduledPlaceholder?: string;
  };
  publishType: PublishType;
  publishTime?: Date | null;
  errorText?: string;
  onPublishTypeChange: (type: PublishType) => void;
  onPublishTimePress?: (anchor?: AnchorRect) => void;
}

export const PublishOptionsSectionBase = memo<PublishOptionsSectionBaseProps>(({
  title,
  copy,
  publishType,
  publishTime,
  errorText,
  onPublishTypeChange,
  onPublishTimePress,
}) => {
  const styles = useStyles();
  const { admin } = useAdminColors();

  const renderPublishTypeOption = (type: PublishType, label: string, description: string) => {
    const selected = publishType === type;
    return (
      <TouchableOpacity
        key={type}
        onPress={() => onPublishTypeChange(type)}
        activeOpacity={0.85}
        style={styles.publishOptionWrapper}
      >
        <GlassCard
          padding={16}
          style={[styles.publishOption, selected && styles.publishOptionSelected]}
        >
          <View style={styles.publishOptionContent}>
            <View style={[styles.radioButton, selected && styles.radioButtonSelected]}>
              {selected && <View style={styles.radioButtonInner} />}
            </View>
            <View style={styles.publishOptionText}>
              <Text style={styles.publishOptionLabel}>{label}</Text>
              <Text style={styles.publishOptionDescription}>{description}</Text>
            </View>
          </View>
        </GlassCard>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>

      {renderPublishTypeOption('publishNow', copy.publishNow.label, copy.publishNow.description)}
      {renderPublishTypeOption('draft', copy.draft.label, copy.draft.description)}
      {renderPublishTypeOption('scheduled', copy.scheduled.label, copy.scheduled.description)}

      {publishType === 'scheduled' && onPublishTimePress && (
        <View style={styles.scheduledTimeContainer}>
          <DateTimePickerButton
            label={copy.scheduledFieldLabel || 'Publish Time *'}
            value={publishTime || new Date()}
            onPress={onPublishTimePress}
            error={errorText}
            placeholder={copy.scheduledPlaceholder || 'Select date and time'}
          />
        </View>
      )}
    </View>
  );
});

PublishOptionsSectionBase.displayName = 'PublishOptionsSectionBase';

const useStyles = () =>
  useThemedStyles(({ components, colors }) => {
    const admin = components.admin;
    return StyleSheet.create({
      section: {
        marginBottom: 24,
      },
      sectionTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: admin.dataTable.text,
        marginBottom: 16,
      },
      publishOptionWrapper: {
        marginBottom: 12,
      },
      publishOption: {
        // GlassCard already supplies bg + border; selected variant tints below.
      },
      publishOptionSelected: {
        borderColor: admin.formInput.borderFocused,
        backgroundColor: admin.selector.itemSelectedBg,
      },
      publishOptionContent: {
        flexDirection: 'row',
        alignItems: 'flex-start',
      },
      radioButton: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 2,
        borderColor: admin.dataTable.border,
        marginRight: 12,
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 2,
      },
      radioButtonSelected: {
        borderColor: admin.formInput.borderFocused,
      },
      radioButtonInner: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: admin.formInput.borderFocused,
      },
      publishOptionText: {
        flex: 1,
      },
      publishOptionLabel: {
        fontSize: 16,
        fontWeight: '500',
        color: admin.dataTable.text,
        marginBottom: 4,
      },
      publishOptionDescription: {
        fontSize: 14,
        color: colors.text.dim,
        lineHeight: 20,
      },
      scheduledTimeContainer: {
        marginTop: 16,
        paddingLeft: 32,
      },
    });
  });
